import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import * as config from 'config'
import { AppServices } from 'middleware'
import type { State } from 'store'
import type { AuthTicket } from 'http/oauthService'
import { toast } from 'sonner'
import { Chatbot, ChatbotHandle } from '@convious/chatbot-ui'
import { LucideIcon } from 'utils/lucideIcon'
import { isLocalStorageAvailable } from 'utils/storage'
import { useAssistantAccess } from 'pulse/hooks/usePulseAccess'
import { useTranslation } from 'translations'
import '@convious/chatbot-ui/styles.css'
import './chatbotDock.scss'

export interface AskOptions {
    page?: string
    context?: Record<string, unknown>
}

interface ChatbotValue {
    ask: (text?: string, options?: AskOptions) => void
    open: (options?: AskOptions) => void
    close: () => void
    isOpen: boolean
    unread: boolean
}

const ChatbotContext = createContext<ChatbotValue | null>(null)

const CHATBOT_API = import.meta.env.VITE_CHATBOT_API || 'https://chatbot-api.staging.convious.com'
const backofficeEndpoint = config.getRequired('backoffice-endpoint')

const PANEL_SLIDE_IN_MS = 300
const REPLY_PREVIEW_MAX = 300

const DOCK_WIDTH_KEY = 'chatbot-dock-width'
const DOCK_MIN = 320
const DOCK_DEFAULT = 703
const DOCK_MAX = 880
const MAIN_MIN = 680

function clampDockWidth(width: number): number {
    if (typeof window === 'undefined') return DOCK_DEFAULT
    const max = Math.max(DOCK_MIN, Math.min(DOCK_MAX, window.innerWidth - MAIN_MIN))
    return Math.max(DOCK_MIN, Math.min(max, width))
}

function storedDockWidth(): number {
    if (!isLocalStorageAvailable()) return DOCK_DEFAULT
    const raw = Number(window.localStorage.getItem(DOCK_WIDTH_KEY))
    return clampDockWidth(Number.isFinite(raw) && raw > 0 ? raw : DOCK_DEFAULT)
}

function isAssistantRoute(pathname: string): boolean {
    return /^\/account\/[^/]+\/dashboard\/overview\/?$/.test(pathname)
}

function requestUrl(input: RequestInfo | URL): string {
    return typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
}

function isChatStreamUrl(url: string): boolean {
    return url.startsWith(CHATBOT_API) && url.includes('/chat/stream')
}

function withReplyPreview(res: Response, onReplyFinished: (preview: string) => void): Response {
    const decoder = new TextDecoder()
    let pending = ''
    let preview = ''
    const tap = new TransformStream<Uint8Array, Uint8Array>({
        transform: (chunk, controller) => {
            controller.enqueue(chunk)
            if (preview.length > REPLY_PREVIEW_MAX) return
            pending += decoder.decode(chunk, { stream: true })
            const lines = pending.split('\n')
            pending = lines.pop() ?? ''
            for (const line of lines) {
                if (!line.startsWith('data:')) continue
                try {
                    const ev = JSON.parse(line.replace(/^data:\s*/, ''))
                    if (ev?.type === 'text-delta' && typeof ev.text === 'string') preview += ev.text
                } catch {
                    continue
                }
            }
        },
        flush: () => onReplyFinished(preview),
    })
    return new Response(res.body!.pipeThrough(tap), {
        status: res.status,
        statusText: res.statusText,
        headers: res.headers,
    })
}

export function ChatbotProvider({ children }: { children: React.ReactNode }) {
    const [chatOpen, setChatOpen] = useState(false)
    const [page, setPage] = useState<string | undefined>(undefined)
    const [context, setContext] = useState<Record<string, unknown> | undefined>(undefined)
    const chatRef = useRef<ChatbotHandle>(null)
    const { t } = useTranslation()
    const history = useHistory()
    const chatOpenRef = useRef(chatOpen)
    chatOpenRef.current = chatOpen

    const focusTimer = useRef<number | null>(null)
    const focusComposerOnceSlideInEnds = useCallback(() => {
        if (focusTimer.current != null) window.clearTimeout(focusTimer.current)
        focusTimer.current = window.setTimeout(() => {
            focusTimer.current = null
            if (!chatOpenRef.current) return
            document.querySelector<HTMLTextAreaElement>('.chatbot-ui-root textarea')?.focus()
        }, PANEL_SLIDE_IN_MS + 50)
    }, [])

    const account = useSelector<State, string | null>((s) => s.preferences.activeAccount)
    const assistantAllowed = useAssistantAccess(account ?? undefined)
    const ticket = useSelector<State, AuthTicket | null>((s) => s.auth.ticket)
    const ticketRef = useRef(ticket)
    ticketRef.current = ticket

    const getAuthToken = useCallback(async () => {
        const current = ticketRef.current
        if (!current) {
            return null
        }
        try {
            const fresh = await AppServices.loginService.ensureTokenNotExpired(current)
            return fresh.accessToken
        } catch {
            return null
        }
    }, [])

    const layoutRef = useRef<HTMLDivElement>(null)
    const flipOpen = useCallback((next: boolean) => {
        const doc = document as Document & {
            startViewTransition?: (update: () => void) => { finished: Promise<void> }
        }
        if (!doc.startViewTransition || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setChatOpen(next)
            return
        }
        layoutRef.current?.classList.add('no-dock-slide')
        const transition = doc.startViewTransition(() => {
            flushSync(() => setChatOpen(next))
            flushSync(() => window.dispatchEvent(new Event('layout-remeasure')))
        })
        transition.finished.finally(() => {
            layoutRef.current?.classList.remove('no-dock-slide')
            window.dispatchEvent(new Event('resize'))
        })
    }, [])

    const ask = useCallback((text?: string, options?: AskOptions) => {
        setPage(options?.page)
        setContext(options?.context)
        flipOpen(true)
        if (text) {
            chatRef.current?.sendMessage(text, {
                newConversation: true,
                page: options?.page,
                context: options?.context,
            })
        } else {
            chatRef.current?.startNewConversation()
        }
        focusComposerOnceSlideInEnds()
    }, [flipOpen, focusComposerOnceSlideInEnds])

    const open = useCallback((options?: AskOptions) => {
        if (options) {
            setPage(options.page)
            setContext(options.context)
        }
        flipOpen(true)
    }, [flipOpen])
    const close = useCallback(() => flipOpen(false), [flipOpen])

    const { pathname } = useLocation()
    useEffect(() => {
        if (!isAssistantRoute(pathname)) setChatOpen(false)
    }, [pathname])

    useEffect(() => {
        if (!chatOpen) return
        const onKeyDown = (ev: KeyboardEvent) => {
            if (ev.key !== 'Escape' || ev.defaultPrevented) return
            flipOpen(false)
        }
        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [chatOpen, flipOpen])

    const prevAccount = useRef(account)
    useEffect(() => {
        if (prevAccount.current === account) {
            return
        }
        prevAccount.current = account
        setChatOpen(false)
        setPage(undefined)
        setContext(undefined)
    }, [account])

    const [unread, setUnread] = useState(false)
    const replyToastId = useRef<string | number | null>(null)

    const openOnAssistantRoute = useCallback(() => {
        if (!isAssistantRoute(history.location.pathname)) {
            history.push(`/account/${account}/dashboard/overview`)
        }
        open()
    }, [account, history, open])

    const announceReply = useCallback(
        (text: string) => {
            if (chatOpenRef.current) return
            setUnread(true)
            replyToastId.current = toast('Convious AI', {
                description: text.trim() || t('chatbot.replyReady'),
                duration: 8000,
                action: { label: t('chatbot.open'), onClick: openOnAssistantRoute },
            })
        },
        [openOnAssistantRoute, t],
    )
    const announceReplyRef = useRef(announceReply)
    announceReplyRef.current = announceReply

    useEffect(
        function clearUnreadOnceOpened() {
            if (!chatOpen) return
            setUnread(false)
            if (replyToastId.current != null) {
                toast.dismiss(replyToastId.current)
                replyToastId.current = null
            }
        },
        [chatOpen],
    )

    useEffect(function watchChatStreamForFinishedReplies() {
        const originalFetch = window.fetch
        window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
            const res = await originalFetch(input, init)
            if (!isChatStreamUrl(requestUrl(input)) || !res.ok || !res.body) return res
            return withReplyPreview(res, (preview) => announceReplyRef.current(preview))
        }
        return () => {
            window.fetch = originalFetch
        }
    }, [])

    const onNavigate = useCallback(
        (path: string) => {
            const withoutAccount = path.replace(/^\/account\/[^/]+/, '')
            const clean = withoutAccount.startsWith('/') ? withoutAccount : `/${withoutAccount}`
            history.push(`/account/${account}${clean}`)
        },
        [account, history],
    )

    const value = useMemo<ChatbotValue>(
        () => ({ ask, open, close, isOpen: chatOpen, unread }),
        [ask, open, close, chatOpen, unread],
    )

    const [dockWidth, setDockWidth] = useState(storedDockWidth)
    const [dragging, setDragging] = useState(false)
    const announceLayout = () => window.dispatchEvent(new Event('resize'))

    const dragFrame = useRef<number | null>(null)
    const onGripPointerDown = (down: React.PointerEvent<HTMLDivElement>) => {
        down.preventDefault()
        down.currentTarget.setPointerCapture(down.pointerId)
        setDragging(true)
    }
    const onGripPointerMove = (move: React.PointerEvent<HTMLDivElement>) => {
        if (!move.currentTarget.hasPointerCapture(move.pointerId)) return
        const right = layoutRef.current?.getBoundingClientRect().right
        if (right == null) return
        const next = clampDockWidth(Math.round(right - move.clientX))
        if (dragFrame.current != null) return
        dragFrame.current = window.requestAnimationFrame(() => {
            dragFrame.current = null
            setDockWidth(next)
            announceLayout()
        })
    }
    const onGripPointerUp = (up: React.PointerEvent<HTMLDivElement>) => {
        up.currentTarget.releasePointerCapture(up.pointerId)
        setDragging(false)
        if (isLocalStorageAvailable()) window.localStorage.setItem(DOCK_WIDTH_KEY, String(dockWidth))
        announceLayout()
    }
    const onGripDoubleClick = () => {
        const next = clampDockWidth(DOCK_DEFAULT)
        setDockWidth(next)
        if (isLocalStorageAvailable()) window.localStorage.setItem(DOCK_WIDTH_KEY, String(next))
    }

    const onGripKeyDown = (key: React.KeyboardEvent<HTMLDivElement>) => {
        const step = key.key === 'ArrowLeft' ? 24 : key.key === 'ArrowRight' ? -24 : 0
        if (!step) return
        key.preventDefault()
        const next = clampDockWidth(dockWidth + step)
        setDockWidth(next)
        if (isLocalStorageAvailable()) window.localStorage.setItem(DOCK_WIDTH_KEY, String(next))
        announceLayout()
    }

    if (!assistantAllowed) {
        return (
            <ChatbotContext.Provider value={NO_CHATBOT}>
                <div className="chatbot-dock-layout" ref={layoutRef}>
                    <div className="chatbot-dock-main">{children}</div>
                </div>
            </ChatbotContext.Provider>
        )
    }

    return (
        <ChatbotContext.Provider value={value}>
            <div className="chatbot-dock-layout" ref={layoutRef}>
                <div className="chatbot-dock-main">{children}</div>
                <aside
                    className={`chatbot-dock${chatOpen ? ' open' : ''}${dragging ? ' dragging' : ''}`}
                    style={{ width: chatOpen ? dockWidth : 0 }}
                    aria-hidden={!chatOpen}
                    aria-label="Convious AI"
                    onTransitionEnd={(ev) => {
                        if (ev.propertyName === 'width') announceLayout()
                    }}
                >
                    <div
                        className="chatbot-dock-grip"
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={t('chatbot.resizePanel')}
                        tabIndex={chatOpen ? 0 : -1}
                        onPointerDown={onGripPointerDown}
                        onPointerMove={onGripPointerMove}
                        onPointerUp={onGripPointerUp}
                        onKeyDown={onGripKeyDown}
                        onDoubleClick={onGripDoubleClick}
                    />
                    <div className="chatbot-dock-clip">
                        <div className="chatbot-dock-inner" style={{ width: dockWidth }}>
                            <button
                                type="button"
                                className="chatbot-dock-close"
                                aria-label={t('chatbot.hideConversation')}
                                tabIndex={chatOpen ? 0 : -1}
                                onClick={close}
                            >
                                <LucideIcon iconName="PanelRightClose" size={18} strokeWidth={2} />
                            </button>
                            {ticket && account && (
                                <Chatbot
                                    key={account}
                                    ref={chatRef}
                                    mode="inline"
                                    accountSlug={account}
                                    page={page}
                                    context={context}
                                    apiBaseUrl={CHATBOT_API}
                                    getAuthToken={getAuthToken}
                                    title="Convious AI"
                                    showSettings={false}
                                    showPromptMenu={false}
                                    showExpertToggle={false}
                                    onNavigate={onNavigate}
                                    backofficeBaseUrl={backofficeEndpoint}
                                    showSuggestions={false}
                                />
                            )}
                        </div>
                    </div>
                </aside>
            </div>
        </ChatbotContext.Provider>
    )
}

const NO_CHATBOT: ChatbotValue = {
    ask: () => undefined,
    open: () => undefined,
    close: () => undefined,
    isOpen: false,
    unread: false,
}

export function useChatbot(): ChatbotValue {
    return useContext(ChatbotContext) ?? NO_CHATBOT
}
